import i18n from "@/languageConfig"

export interface FollowupSuggestion {
  question: string
  miniPlan: string[]
}

/**
 * Compose a user-facing question that includes the mini-plan hint inline
 * so the agent picks up where the previous turn left off without repeating
 * the whole /plan.md process from scratch.
 */
export function composeFollowupQuestion(suggestion: FollowupSuggestion): string {
  if (suggestion.miniPlan.length === 0) return suggestion.question
  const planBullets = suggestion.miniPlan.map((s) => `- ${s}`).join("\n")
  return `${suggestion.question}\n\n_${i18n.t("deepExplorer.smart_followups.composed_plan_intro")}_:\n${planBullets}`
}

/**
 * Detect whether a stored user message was composed by `composeFollowupQuestion`.
 * Used by the chat renderer to swap in a stylized "Follow-up" pill that hides
 * the inline plan bullets from the user (the agent still receives them).
 *
 * The marker is the localized intro string wrapped in italics — checked across
 * all language packs so the detection still works after a language switch
 * mid-session.
 */
export function parseFollowupMessage(message: string): {
  isFollowup: boolean
  question: string
} {
  const introKey = "deepExplorer.smart_followups.composed_plan_intro"
  // Probe every loaded language so a session that started in en still detects
  // a message composed in sv (and vice versa).
  const candidates = new Set<string>()
  const languages = i18n.languages.length ? i18n.languages : [i18n.language || "en"]
  for (const lng of languages) {
    const intro = i18n.t(introKey, { lng })
    if (intro && intro !== introKey) candidates.add(`\n\n_${intro}_:\n`)
  }
  for (const marker of candidates) {
    const idx = message.indexOf(marker)
    if (idx !== -1) {
      return { isFollowup: true, question: message.slice(0, idx).trim() }
    }
  }
  return { isFollowup: false, question: message }
}

/**
 * Parse /followups.json contents (as written by the executor) into the
 * shape the Zustand store and SmartFollowupCards expect.
 *
 * Mirrors the live-stream parser in use-ai-explorer-chat.ts (the
 * deep_followup_suggestions handler) so reload behaviour matches what the
 * user saw at end-of-stream. Tolerant of:
 *  - undefined / empty content (fresh sessions, smart-followups disabled)
 *  - markdown code-fence wrappers the model occasionally emits
 *  - malformed JSON (returns [], doesn't throw)
 *  - the wire format mismatch: /followups.json is a top-level array,
 *    while the deep_followup_suggestions stream frame wraps it in a
 *    {suggestions: [...]} envelope. We accept either.
 */
export function parseFollowupsFromFile(content: string | undefined): FollowupSuggestion[] {
  if (!content) return []
  let text = content.trim()
  if (text.startsWith("```")) {
    text = text.replace(/^```[a-z]*\n/i, "").replace(/\n```\s*$/, "")
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return []
  }
  let list: unknown[] = []
  if (Array.isArray(parsed)) {
    list = parsed
  } else {
    const suggestions = (parsed as { suggestions?: unknown }).suggestions
    if (Array.isArray(suggestions)) {
      list = suggestions
    }
  }
  return list
    .map((item): FollowupSuggestion | null => {
      if (!item || typeof item !== "object") return null
      const record = item as Record<string, unknown>
      const question = String(record.question ?? "").trim()
      const planRaw = record.mini_plan ?? record.miniPlan ?? []
      const miniPlan = Array.isArray(planRaw)
        ? planRaw.map((s) => String(s).trim()).filter(Boolean)
        : []
      if (!question || miniPlan.length === 0) return null
      return { question, miniPlan }
    })
    .filter((s): s is FollowupSuggestion => s !== null)
}
